import type { OrderRow, OrderDetail } from '../model/order-schema';

/* eslint-disable @typescript-eslint/prefer-nullish-coalescing --
   Ported verbatim from cpanel. The `||` fallbacks are INTENTIONAL falsy-collapse
   (e.g. `service_id: 0` must fall through to the default shop service; an empty
   `business_name` must fall through to `name`). Switching to `??` would change
   behavior. Preserve byte-for-byte. */

/**
 * Order-service (vendor category) presentation + client-type classification.
 * Ported verbatim from cpanel `model/helpers.tsx`. The row-background tint and
 * the service icon are driven by `service_id` (with `xolodilnik` →fridge).
 */

type ServiceKey = 1 | 2 | 3 | 23 | 'fridge';

interface ServiceMeta {
  /** Row background tint. */
  bg: string;
  /** Public asset path (served from `/public`). */
  photo: string;
}

const ORDER_TYPE_BY_ID: Record<ServiceKey, ServiceMeta> = {
  1: { bg: 'transparent', photo: 'service_flower.png' }, // Gullar
  2: { bg: '#F0F9FF', photo: 'service_pharma.png' }, // Dorixona
  3: { bg: 'transparent', photo: 'service_gift.png' }, // Sovg'alar
  23: { bg: '#FEFAF5', photo: 'service_shop.png' }, // Do'kon
  fridge: { bg: '#FEFBE8', photo: 'service_fridge.png' }, // Fridge
};

/** Default to the SHOP service (id 23) when data/service is missing. */
export function getOrderService(
  data: Pick<OrderRow, 'vendor_order_id' | 'service_id'> | null | undefined,
): ServiceMeta {
  if (!data) return ORDER_TYPE_BY_ID[23];
  const key = (
    data.vendor_order_id === 'xolodilnik' ? 'fridge' : data.service_id || 23
  ) as ServiceKey;
  return ORDER_TYPE_BY_ID[key] ?? ORDER_TYPE_BY_ID[23];
}

export type OrderClientStatusCode = 'legal' | 'client' | 'phys';

export interface OrderClientType {
  /** Russian source label. */
  status: string;
  /** StatusBadge tone for the v2 badge. */
  tone: 'pending' | 'active' | 'success';
  /** Vendor display title (uppercased). */
  title: string;
  /** Stable code for i18n + first-letter badge. */
  statusCode: OrderClientStatusCode;
}

/**
 * Classify an order's client/vendor into legal / client / physical person.
 * Ported from cpanel `getOrderClientType` (MUI palette tokens → v2 tone slugs).
 */
export function getOrderClientType(
  data: Pick<
    OrderRow & OrderDetail,
    'is_business' | 'is_integration' | 'payment_type' | 'vendor'
  >,
): OrderClientType {
  const orderIsEntity = data?.is_business || data?.is_integration;
  const vb = data?.vendor?.is_business;
  const vc = data?.vendor?.is_corporate;
  const status =
    data?.payment_type === 'BALANCE'
      ? 'Юр. Лицо'
      : !orderIsEntity
        ? 'Клиент'
        : vb && vc
          ? 'Юр. Лицо'
          : 'Физ. Лицо';

  // legal → pending(amber), client → active(brand), phys → success(green).
  const tone =
    status === 'Юр. Лицо' ? 'pending' : status === 'Клиент' ? 'active' : 'success';
  const statusCode: OrderClientStatusCode =
    status === 'Юр. Лицо' ? 'legal' : status === 'Клиент' ? 'client' : 'phys';
  const title = data?.is_business
    ? data?.vendor?.business_name?.toUpperCase() ||
      data?.vendor?.name?.toUpperCase() ||
      'Клиент'
    : data?.vendor?.name?.toUpperCase() || 'Клиент';

  return { status, tone, title, statusCode };
}
